export interface IPermitTypeLike {
  id: string
  type: string
  isEnabled: boolean
  isAdminOnly: boolean
}

export interface IResolveFormTypeDeepLinkParams {
  deepLinkFormType: string | null
  handledFormType: string | null
  isDataReceived: boolean
  enabledTypes: IPermitTypeLike[]
  allTypes: IPermitTypeLike[]
}

export type ResolveFormTypeResult =
  | { action: 'skip' }
  | { action: 'wait' }
  | { action: 'open'; permitTypeId: string; handledFormType: string }
  | { action: 'deny'; handledFormType: string }
  | { action: 'not_found'; handledFormType: string }

/**
 * Pure decision logic for resolving a `?formType=` deep link.
 *
 * Returns which action the caller should take, without side effects.
 * Both desktop and mobile Logistics components delegate to this function
 * so the branching logic is tested in one place.
 */
export function resolveFormTypeDeepLink(
  params: IResolveFormTypeDeepLinkParams,
): ResolveFormTypeResult {
  const {
    deepLinkFormType,
    handledFormType,
    isDataReceived,
    enabledTypes,
    allTypes,
  } = params

  if (!deepLinkFormType) {
    return { action: 'skip' }
  }

  if (handledFormType === deepLinkFormType) {
    return { action: 'skip' }
  }

  if (!isDataReceived) {
    return { action: 'wait' }
  }

  const permitType = enabledTypes.find(pt => pt.type === deepLinkFormType)

  if (permitType) {
    return {
      action: 'open',
      permitTypeId: permitType.id,
      handledFormType: deepLinkFormType,
    }
  }

  const exists = allTypes.some(pt => pt.type === deepLinkFormType)

  if (exists) {
    return { action: 'deny', handledFormType: deepLinkFormType }
  }

  return { action: 'not_found', handledFormType: deepLinkFormType }
}
